import { Logger } from '@/Logger';

import type {
  AnalysisResultData,
  AnalysisStatistics,
  APITeoMarker,
  AssistantInstruction,
  ExcerptContext,
  SubEvaluation,
  TextBlock,
} from '../TeoApi';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { TeoText } from './TeoText';

const LOG = Logger('AnalysisResult');

export type AnalysisResultEvents = {
  currentInstructionChanged: (detail: AssistantInstruction | undefined) => void;
};

export class AnalysisResult {
  private _currentInstruction: AssistantInstruction | undefined;
  events = new TeoEventTarget<AnalysisResultEvents>();
  private relationIdCounter = 10000000;

  readonly createdAt;

  readonly statistics: AnalysisStatistics;

  constructor(
    public data: AnalysisResultData,
    private text: TeoText,
    private analysedText: string | TextBlock[],
    createdAt = Date.now(),
  ) {
    this.data.markers.forEach((marker) => {
      marker.groupName = 'analysis';
      if (marker.relation === null) {
        marker.relation = String(this.relationIdCounter++);
      }
    });
    this.statistics = this.data.additional_values;
    this.createdAt = createdAt;
  }

  get currentInstruction(): AssistantInstruction | undefined {
    return this._currentInstruction;
  }

  getMarkers(): APITeoMarker[] {
    return this.data.markers;
  }

  /**
   * Ids of all paragraph markers, in document order. The paragraph contents are not
   * taken from here: they are read from the editor, so that they reflect edits made
   * since this analysis was created.
   */
  getParagraphMarkerIds(): string[] {
    const ids = this.getMarkers()
      .filter((marker) => marker.marker.startsWith('paragraph') && marker.length > 0)
      .sort((a, b) => a.offset - b.offset)
      .map((marker) => marker.relation)
      .filter((relation): relation is string => !!relation);
    return Array.from(new Set(ids));
  }

  getInstructions(): AssistantInstruction[] {
    return this.data.instructions;
  }

  getIndices(): Record<string, SubEvaluation> {
    return this.data.indices;
  }

  getTPI(): number {
    return this.data.tpi;
  }

  getCurrentTextPropertyMarkerNames(): string[] | undefined {
    const dimension = this.text.ui.state.dimension;
    const textProperty = this.text.ui.state.textProperty;
    LOG.dev(
      '@.getCurrentTextPropertyMarkerNames',
      this.text.ui.state,
      this.data.indices[dimension ?? '']?.text_properties[textProperty ?? '']?.marker_names,
    );
    if (dimension && textProperty) {
      return this.data.indices[dimension]?.text_properties[textProperty]?.marker_names;
    }
  }

  getNumWords(): number {
    return this.data.additional_values.num_words ?? 0;
  }

  getTodoIndexName(): string | undefined {
    if (this.isGood()) return;
    return this.getPriorityInstruction()?.index_name;
  }

  hasWhitelistedWords(): boolean {
    return this.data.additional_values.num_whitelisted_words > 0;
  }

  getPriorityInstruction(): AssistantInstruction | undefined {
    return this.data.instructions[0];
  }

  isGood(): boolean {
    return this.data.tpi >= this.text.core.staticConfig.tpiTarget;
  }

  updateCurrentInstruction(
    dimension: string | undefined,
    instructionsToSkip: number[] = [],
    checkPriority = false,
  ): AssistantInstruction | undefined {
    const instruction = this.checkCurrentInstruction(dimension, instructionsToSkip, checkPriority);
    if (instruction !== this._currentInstruction) {
      this._currentInstruction = instruction;
      this.events.triggerEvent('currentInstructionChanged', instruction);
    }
    return this._currentInstruction;
  }

  /**
   * Retrieves the most relevant instruction based on the provided dimension.
   * Instructions whose ids are in `instructionsToSkip` are ignored, and the next remaining
   * instruction in list order is selected. When `checkPriority` is set, a `priority_score`
   * of 0 is ignored as well. Returns `undefined` if none remain.
   */
  private checkCurrentInstruction(
    dimension: string | undefined,
    instructionsToSkip: number[] = [],
    checkPriority = false,
  ): AssistantInstruction | undefined {
    const candidates = this.data.instructions.filter(
      (instruction) =>
        !instructionsToSkip.includes(instruction.id) &&
        (!checkPriority || instruction.priority_score > 0),
    );
    if (candidates.length === 0) return;
    if (dimension) {
      const partlyMatchingInstruction = candidates.find(
        (instruction) => instruction.index_name === dimension,
      );
      if (partlyMatchingInstruction) return partlyMatchingInstruction;
    }
    return candidates[0];
  }

  extractMarkerInner(marker: APITeoMarker): string {
    return this.extractSubstring(marker.offset, marker.offset + marker.length);
  }

  extractMarkerBefore(marker: APITeoMarker): string {
    return this.extractSubstring(0, marker.offset);
  }

  extractMarkerAfter(marker: APITeoMarker): string {
    return this.extractSubstring(marker.offset + marker.length, Number.MAX_SAFE_INTEGER);
  }

  private extractSubstring(start: number, end: number): string {
    if (start < 0 || end < start) {
      throw new Error(`Start or end index is invalid.`);
    }
    if (typeof this.analysedText === 'string') {
      if (end > this.analysedText.length) {
        end = this.analysedText.length;
      }
      return this.analysedText.substring(start, end);
    } else {
      return this.extractFromTextBlocks(this.analysedText, start, end);
    }
  }

  private extractFromTextBlocks(textBlocks: TextBlock[], start: number, end: number): string {
    let result = '';
    let currentOffset = 0;
    for (const block of textBlocks) {
      const blockEnd = currentOffset + (block.length || block.string.length);
      if (blockEnd < start) {
        currentOffset = blockEnd;
        continue;
      }
      if (currentOffset > end) {
        break;
      }
      const blockStart = Math.max(currentOffset, start);
      const blockEndAdjusted = Math.min(blockEnd, end);
      const blockString = block.string.substring(
        blockStart - currentOffset,
        blockEndAdjusted - currentOffset,
      );
      result += blockString;
      currentOffset = blockEnd;
    }
    return result;
  }

  getParentMarker(marker: APITeoMarker, parentType: string): APITeoMarker | undefined {
    return this.getMarkers().find(
      (m) =>
        m.marker.startsWith(parentType) &&
        m.offset <= marker.offset &&
        m.offset + m.length >= marker.offset + marker.length,
    );
  }

  /**
   * @deprecated Use `MarkerManager.createMarkerContext` to read live editor text.
   * This method slices the analysed snapshot and is stale after edits.
   */
  createMarkerContext(
    marker: APITeoMarker,
    replacementId: string,
    parentType?: string,
  ): ExcerptContext {
    const text = this.extractMarkerInner(marker);
    const markerAround = parentType ? (this.getParentMarker(marker, parentType) ?? marker) : marker;
    const context: ExcerptContext = {
      text: this.extractMarkerInner(markerAround),
      textBefore: this.extractMarkerBefore(markerAround),
      textAfter: this.extractMarkerAfter(markerAround),
      textFocus: text,
      replacementWrapper: markerAround.relation ?? '',
    };
    return context;
  }

  toJSON() {
    return {
      data: this.data,
      analysedText: this.analysedText,
      createdAt: this.createdAt,
    };
  }

  static fromJSON(text: TeoText, json: ReturnType<AnalysisResult['toJSON']>) {
    return new AnalysisResult(json.data, text, json.analysedText, json.createdAt);
  }
}

export type AnalysisJSON = ReturnType<AnalysisResult['toJSON']>;
